import { z } from 'zod';
import { ZodValidationErrorInterface } from '@/components/app-ui/form/shared';
import { INPUT_LIMITS, emailResolver, wmv } from '@/constants/form.constants';

interface SignupFormDataInterface {
	firstName: string;
	lastName: string;
	email: string;
	password?: string;
	organization: string;
	phone?: string;
	profession: string;
	leadSource: string;
	studentDeviceUsage: string;
}

const getStepOneSchema = (needPassword: boolean) => {
	return z.object({
		firstName: wmv(z.string().min(1, { message: 'First name is required' })),
		lastName: wmv(z.string().min(1, { message: 'Last name is required' })),
		email: emailResolver(),
		password: needPassword
			? z
					.string()
					.min(1, 'Password is required')
					.refine(
						(value) => {
							const hasLetter = /[a-zA-Z]/.test(value);
							const hasNumber = /[0-9]/.test(value);
							const hasValidLength = value.length >= INPUT_LIMITS.PASSWORD_MIN;
							return hasLetter && hasNumber && hasValidLength;
						},
						{
							message: `Password must have a letter, a number, and at least ${INPUT_LIMITS.PASSWORD_MIN} characters`,
						},
					)
			: z.string().optional(),
		organization: wmv(z.string().min(1, { message: 'Organization is required' })),
		phone: wmv(z.string().optional()),
	});
};

const stepOneResolver = (data: SignupFormDataInterface, needPassword: boolean) => {
	try {
		getStepOneSchema(needPassword).parse(data);
		return { values: data, errors: {} };
	} catch (error: any) {
		if (error instanceof z.ZodError) {
			const issues = error.issues;
			const errors = issues.reverse().reduce((acc: ZodValidationErrorInterface, issue) => {
				acc[issue.path[0]] = {
					message: issue.message,
				};
				return acc;
			}, {});
			return { values: {}, errors };
		} else return { values: data, errors: {} };
	}
};

const stepTwoSchema = z.object({
	profession: z.string().min(1, { message: 'Please select from the options' }),
	leadSource: z.string().min(1, { message: 'Please select from the options' }),
	studentDeviceUsage: z.string().min(1, { message: 'Please select from the options' }),
});

const stepTwoResolver = (data: SignupFormDataInterface) => {
	try {
		stepTwoSchema.parse(data);
		return { values: data, errors: {} };
	} catch (error: any) {
		if (error instanceof z.ZodError) {
			const issues = error.issues;
			const errors = issues.reverse().reduce((acc: ZodValidationErrorInterface, issue) => {
				acc[issue.path[0]] = {
					message: issue.message,
				};
				return acc;
			}, {});
			return { values: {}, errors };
		} else return { values: data, errors: {} };
	}
};

export const getStepResolver = (step: number, needPassword = true) => {
	if (step === 1) return (data: SignupFormDataInterface) => stepOneResolver(data, needPassword);
	return (data: SignupFormDataInterface) => stepTwoResolver(data);
};
